import { useQuery } from '@tanstack/react-query';
import { ArrowLeftRight } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ApiError } from '@/lib/api';
import { fetchCadeCase, fetchCadeSessions } from '@/services/cade';
import { codedText } from '@/lib/codings';
import type { CadeCaseDecision, CadeCaseDetail, CadeCodedField } from '@/services/types';

/** Digits of a typed case number: the key `/cade/c/:key` resolves. */
function caseKeyOf(typed: string): string {
  return typed.replace(/\D/g, '');
}

/**
 * Two cases on one screen, aligned line by line, so "my client's case is like A and not like
 * B" can be argued from the record. Every line is read from what the case page already shows.
 * The lines a reader looks for first (relevant market, theory of harm, remedy, the fine's base
 * and rate) come from the fields coded from the votes, served values only; a case with none is
 * said to be uncoded rather than left blank, because a blank row reads as "the cases agree".
 *
 * A fine here is an amount minuted on a decision, shown beside that decision's outcome: it is
 * not the penalty imposed (items the session adjourned also carry the proposed amount).
 */
export function CadeComparePage() {
  const { t } = useTranslation(['cade']);
  const [params, setParams] = useSearchParams();
  const a = caseKeyOf(params.get('a') ?? '');
  const b = caseKeyOf(params.get('b') ?? '');
  const [typedA, setTypedA] = useState(params.get('a') ?? '');
  const [typedB, setTypedB] = useState(params.get('b') ?? '');
  // The corpus both cases are read from, stated whether or not two cases are open yet.
  const archive = useQuery({
    queryKey: ['cade', 'compare', 'as-of'],
    queryFn: () => fetchCadeSessions({ limit: 1 }),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const next = new URLSearchParams();
    if (caseKeyOf(typedA)) next.set('a', caseKeyOf(typedA));
    if (caseKeyOf(typedB)) next.set('b', caseKeyOf(typedB));
    setParams(next);
  };
  const swap = () => {
    setTypedA(typedB);
    setTypedB(typedA);
    const next = new URLSearchParams();
    if (b) next.set('a', b);
    if (a) next.set('b', a);
    setParams(next);
  };

  return (
    <div className="space-y-4 p-6" data-compare>
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cade:compare.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cade:compare.subtitle')}</p>
      </header>
      {archive.data ? <AsOfStrip asOf={archive.data.as_of} /> : null}
      <form onSubmit={submit} className="flex flex-wrap items-end gap-2" role="search">
        <label className="space-y-1 text-xs text-muted-foreground">
          {t('cade:compare.caseA')}
          <input
            value={typedA}
            onChange={(event) => setTypedA(event.target.value)}
            placeholder="08700.001234/2020-11"
            className="block w-60 rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
        </label>
        <button
          type="button"
          onClick={swap}
          aria-label={t('cade:compare.swap')}
          className="rounded-md border p-2 text-foreground"
        >
          <ArrowLeftRight className="h-4 w-4" aria-hidden />
        </button>
        <label className="space-y-1 text-xs text-muted-foreground">
          {t('cade:compare.caseB')}
          <input
            value={typedB}
            onChange={(event) => setTypedB(event.target.value)}
            placeholder="08700.004321/2019-05"
            className="block w-60 rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
        </label>
        <button
          type="submit"
          className="rounded-md bg-primary px-4 py-1.5 text-sm text-primary-foreground"
        >
          {t('cade:compare.run')}
        </button>
      </form>
      {a && b ? (
        <Columns a={a} b={b} />
      ) : (
        <p className="text-sm text-muted-foreground" data-compare-prompt>
          {t('cade:compare.prompt')}
        </p>
      )}
    </div>
  );
}

function useCase(key: string) {
  return useQuery({
    queryKey: ['cade', 'case', key],
    queryFn: () => fetchCadeCase(key),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });
}

function Columns({ a, b }: { a: string; b: string }) {
  const { t } = useTranslation(['cade']);
  const first = useCase(a);
  const second = useCase(b);
  if (first.isPending || second.isPending) return <SkeletonLoader rows={8} />;
  const problem = [first, second].find((q) => q.error);
  if (problem?.error) {
    const missing = problem.error instanceof ApiError && problem.error.status === 404;
    return (
      <ErrorMessage
        message={missing ? t('cade:compare.notFound') : problem.error.message}
        onRetry={missing ? undefined : () => void problem.refetch()}
      />
    );
  }
  if (!first.data || !second.data) return null;
  return <Table left={first.data} right={second.data} />;
}

/** The decisions that stand: the case's final ones, falling back to all when none is marked. */
function standing(c: CadeCaseDetail): CadeCaseDecision[] {
  const finals = c.decisions.filter((d) => d.is_final_observed);
  return finals.length > 0 ? finals : c.decisions;
}

function unique(values: (string | null)[]): string[] {
  return [...new Set(values.filter((v): v is string => Boolean(v)))];
}

function Table({ left, right }: { left: CadeCaseDetail; right: CadeCaseDetail }) {
  const { t, i18n } = useTranslation(['cade']);
  const money = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  const day = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeZone: 'UTC' }).format(
      new Date(`${iso}T00:00:00Z`),
    );
  const sharedCitations = new Set(
    standing(left)
      .flatMap((d) => d.legal_citations)
      .filter((c) => standing(right).some((d) => d.legal_citations.includes(c))),
  );

  const rows: { label: string; cells: [ReactNode, ReactNode]; key: string }[] = [];
  const both = (key: string, make: (c: CadeCaseDetail) => ReactNode) =>
    rows.push({ key, label: t(`cade:compare.rows.${key}`), cells: [make(left), make(right)] });
  const dash = <span className="text-muted-foreground">—</span>;

  both('type', (c) => c.case_type ?? dash);
  both('filing', (c) => c.filing_year ?? dash);
  both('procedure', (c) => c.register.value?.procedure ?? dash);
  both('track', (c) => c.register.value?.track ?? dash);
  both('decidedBy', (c) => c.register.value?.decided_by ?? dash);
  both('operationValue', (c) =>
    c.register.value?.operation_value_brl != null
      ? money.format(c.register.value.operation_value_brl)
      : dash,
  );
  both('duration', (c) =>
    c.register.value?.filing_to_decision_days != null
      ? t('cade:compare.days', { count: c.register.value.filing_to_decision_days })
      : dash,
  );
  both('appeal', (c) => c.register.value?.appeal ?? dash);
  both('relators', (c) => {
    const names = unique(c.decisions.map((d) => d.relator_canonical ?? d.relator));
    return names.length > 0 ? names.join(', ') : dash;
  });
  both('sessions', (c) => c.sessions);
  both('outcomes', (c) => (
    <ul className="flex flex-wrap gap-1">
      {unique(standing(c).flatMap((d) => d.outcome_codes)).map((code) => (
        <li key={code} className="rounded border bg-muted/40 px-1.5 py-0.5 text-foreground">
          {t(`cade:overview.outcomes.${code}`, { defaultValue: code })}
        </li>
      ))}
    </ul>
  ));
  both('fines', (c) => {
    const amounts = standing(c).flatMap((d) => d.fines_brl);
    return amounts.length > 0 ? (
      <span className="tabular-nums">{amounts.map((x) => money.format(x)).join(' · ')}</span>
    ) : (
      dash
    );
  });
  both('citations', (c) => {
    const list = unique(standing(c).flatMap((d) => d.legal_citations));
    return list.length > 0 ? (
      <ul className="flex flex-wrap gap-1">
        {list.map((citation) => (
          <li
            key={citation}
            className={
              sharedCitations.has(citation)
                ? 'rounded bg-primary/15 px-1.5 py-0.5 text-foreground'
                : 'rounded bg-muted px-1.5 py-0.5 text-muted-foreground'
            }
          >
            {citation}
          </li>
        ))}
      </ul>
    ) : (
      dash
    );
  });
  both('milestones', (c) => {
    const events = c.register.value?.events ?? [];
    return events.length > 0 ? (
      <ul className="space-y-0.5 text-xs">
        {events.map((e, index) => (
          <li key={`${e.kind}-${index}`}>
            <span className="tabular-nums text-foreground">{day(e.when)}</span>{' '}
            <span className="text-muted-foreground">
              {t(`cade:case.register.kinds.${e.kind}`, { defaultValue: e.kind })}
            </span>
          </li>
        ))}
      </ul>
    ) : (
      dash
    );
  });

  return (
    <div className="overflow-x-auto rounded-md border" data-compare-table>
      <table className="w-full text-sm">
        <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
          <tr>
            <th className="w-44 px-3 py-2" />
            {[left, right].map((c) => (
              <th key={c.case_key} className="px-3 py-2 text-sm text-foreground">
                <Link
                  to={`/cade/c/${c.case_key}`}
                  className="tabular-nums text-primary underline-offset-4 hover:underline"
                >
                  {c.case_number}
                </Link>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-t align-top">
              <th
                scope="row"
                className="px-3 py-2 text-left text-xs font-medium text-muted-foreground"
              >
                {row.label}
              </th>
              {row.cells.map((cell, index) => (
                <td key={index} className="px-3 py-2 text-foreground">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
          {CODED_ROWS.map(([key, fields]) => {
            const cells = [left, right].map((c) => servedIn(c, fields));
            if (cells.every((cell) => cell.length === 0)) {
              return (
                <tr key={key} className="border-t align-top" data-uncoded={key}>
                  <th
                    scope="row"
                    className="px-3 py-2 text-left text-xs font-medium text-muted-foreground"
                  >
                    {t(`cade:compare.rows.${key}`)}
                  </th>
                  <td colSpan={2} className="px-3 py-2 text-xs text-muted-foreground">
                    {t('cade:compare.uncoded')}
                  </td>
                </tr>
              );
            }
            return (
              <tr key={key} className="border-t align-top" data-coded-row={key}>
                <th
                  scope="row"
                  className="px-3 py-2 text-left text-xs font-medium text-muted-foreground"
                >
                  {t(`cade:compare.rows.${key}`)}
                </th>
                {cells.map((served, index) => (
                  <td key={index} className="px-3 py-2 text-foreground">
                    {served.length > 0 ? (
                      <CodedCell fields={served} />
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {t('cade:compare.uncodedShort')}
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="border-t p-3 text-xs text-muted-foreground">{t('cade:compare.note')}</p>
    </div>
  );
}

/** The coded rows and the served fields each one shows, in reading order. */
const CODED_ROWS: [string, string[]][] = [
  ['harm', ['conduct_type', 'theory_of_harm']],
  ['market', ['product_market', 'geographic_market', 'geographic_scope']],
  ['dosimetry', ['fine_base', 'fine_percentage']],
  ['remedy', ['remedy_type', 'remedy_obligations']],
];

function servedIn(c: CadeCaseDetail, fields: string[]): CadeCodedField[] {
  const served = c.codings?.value?.served ?? [];
  return fields.flatMap((name) => served.filter((f) => f.field === name));
}

/** Each value with its field label and, one click away, the quote it rests on. */
function CodedCell({ fields }: { fields: CadeCodedField[] }) {
  const { t, i18n } = useTranslation(['cade']);
  return (
    <dl className="space-y-1 text-xs">
      {fields.map((field) => (
        <div key={field.field} data-coded-field={field.field}>
          <dt className="text-muted-foreground">
            {t(`cade:codings.fields.${field.field}`, { defaultValue: field.field })}
          </dt>
          {/* The quote sits inside the <dd>: a <details> beside the pair breaks the list. */}
          <dd className="text-sm text-foreground">
            {codedText(field, t, i18n.language)}
            {field.quotes[0] ? (
              <details>
                <summary className="cursor-pointer text-xs text-muted-foreground">
                  {t('cade:codings.quote', { count: field.quotes.length })}
                </summary>
                <blockquote className="mt-1 whitespace-pre-line border-l-2 pl-2 text-xs text-muted-foreground">
                  {field.quotes[0].text}
                </blockquote>
              </details>
            ) : null}
          </dd>
        </div>
      ))}
    </dl>
  );
}
